/**
 * AdsRoasBreakdown — ROAS by ad set and ROAS by specific ad (Ads → Overview).
 * Paid cash only (paid-tagged opt-ins).
 * Data: /api/ads/roas-breakdown (same webinar-date cash the hero ROAS card uses).
 * Funnel filter (All / Webinar / Book Call) + scaling metric cards (Jeremy Haynes' list), Oct 21 2026.
 */
import { Fragment, useEffect, useState } from "react";
import { AdCreativePanel, CreativeToggle } from "./AdCreativePanel";
import { SalesCallsPanel, ClosedLeadsCard } from "./SalesCallsPanel";

/** Full name, wraps instead of truncating; click copies the exact name (as in Ads Manager / Drive). */
function CopyName({ text, className = "" }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };
  return (
    <button type="button" onClick={copy} title="Click to copy full name"
      className={`text-left break-words whitespace-normal cursor-copy hover:text-[var(--brand-primary)] ${className}`}>
      {text}
      <span className={`ml-2 text-[10px] uppercase tracking-wide ${copied ? "text-[var(--brand-primary)]" : "text-muted-foreground/60"}`}>{copied ? "Copied" : "copy"}</span>
    </button>
  );
}

import { useDateRange } from "../../context/DateRangeContext";

export interface RoasBreakdownRow {
  id: string;
  name: string;
  adsetName: string;
  campaignName: string;
  spend: number;
  leads: number;
  closes: number;
  cash: number;
  roas: number | null;
  funnel?: "webinar" | "bookCall";
}

export type FunnelFilter = "all" | "webinar" | "bookCall";

export interface FunnelMetrics {
  spend: number; impressions: number; linkClicks: number;
  cpm: number | null; linkCtr: number | null;
  leads: number; costPerLead: number | null;
  bookedCalls: number; bookingRate: number | null; costPerBookedCall: number | null;
  callsDue: number; showedCalls: number; showRate: number | null;
  closes: number; closeRate: number | null; cpa: number | null;
  cash: number; revenue: number; aov: number | null; cashPerClose: number | null;
  cashPerBookedCall: number | null; cashPerShow: number | null; cashPerLead: number | null; callRatio: number | null;
  webinarRegistrants: number; webinarAttended: number; webinarShowRate: number | null;
}

export interface RoasBreakdownData {
  funnel?: FunnelFilter;
  spendByFunnel?: { webinar: number; bookCall: number };
  bookCallCampaigns?: string[];
  metrics?: FunnelMetrics;
  bookCallLeads?: { counted: number; excludedNoTag: number; excludedTests: number };
  webinars: Array<{ id: string; name: string; date: string }>;
  totals: {
    spend: number;
    topLinePaidCash: number;
    organicCash: number;
    organicLeads: number;
    organicStoryCash?: number;
    organicStoryLeads?: number;
    organicBioCash?: number;
    organicBioLeads?: number;
    organicInstagramCash?: number;
    organicInstagramLeads?: number;
    paidCash: number;
    paidRoas: number | null;
    adsetCash: number;
    exactAdCash: number;
    adsetOnlyCash: number;
    untaggedPaidCash: number;
    untaggedPaidLeads: number;
  };
  adsets: RoasBreakdownRow[];
  ads: RoasBreakdownRow[];
}

type SortKey = "name" | "spend" | "leads" | "closes" | "cash" | "roas";

const money = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v ?? 0);
const money2 = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v ?? 0);
const roasText = (cash: number, spend: number) => (spend > 0 && cash > 0 ? `${(cash / spend).toFixed(2)}x` : spend > 0 ? "—" : cash > 0 ? "no spend" : "—");

function RoasTable({ title, subtitle, rows, nameLabel, showAdset, footerRows, showCreative = false }: {
  showCreative?: boolean;
  title: string;
  subtitle: string;
  rows: RoasBreakdownRow[];
  nameLabel: string;
  showAdset: boolean;
  footerRows: Array<{ label: string; cash: number; leads?: number }>;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("cash");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggleOpen = (id: string) => setOpenIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const colCount = 7 + (showAdset ? 1 : 0);
  const val = (r: RoasBreakdownRow, k: SortKey): number | string => (k === "name" ? r.name : k === "roas" ? (r.spend > 0 ? r.cash / r.spend : -1) : r[k]);
  const sorted = [...rows].sort((a, b) => {
    const x = val(a, sortKey), y = val(b, sortKey);
    const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
    return dir === "asc" ? c : -c || b.spend - a.spend;
  });
  const sort = (k: SortKey) => { if (k === sortKey) setDir(dir === "asc" ? "desc" : "asc"); else { setSortKey(k); setDir("desc"); } };
  const arrow = (k: SortKey) => (sortKey === k ? (dir === "asc" ? " ↑" : " ↓") : "");
  const tSpend = rows.reduce((s, r) => s + r.spend, 0);
  const tCash = rows.reduce((s, r) => s + r.cash, 0);
  const tLeads = rows.reduce((s, r) => s + r.leads, 0);
  const tCloses = rows.reduce((s, r) => s + r.closes, 0);
  const th = "pb-3 px-3 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap text-xs";

  return (
    <div className="bg-card rounded-2xl border border-border overflow-x-auto">
      <div className="p-6 pb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </div>
      {rows.length === 0 ? <p className="text-muted-foreground text-center py-10 px-6 text-sm">No ad data for this period</p> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className={`${th} pl-6 pr-4 text-left`} onClick={() => sort("name")}>{nameLabel}{arrow("name")}</th>
              {showAdset && <th className={`${th} text-left`}>Ad Set</th>}
              <th className={`${th} text-left`}>Campaign</th>
              <th className={`${th} text-right`} onClick={() => sort("spend")}>Spend{arrow("spend")}</th>
              <th className={`${th} text-right`} onClick={() => sort("leads")}>Leads{arrow("leads")}</th>
              <th className={`${th} text-right`} onClick={() => sort("closes")}>Closes{arrow("closes")}</th>
              <th className={`${th} text-right`} onClick={() => sort("cash")}>Cash Collected{arrow("cash")}</th>
              <th className={`${th} text-right pr-6`} onClick={() => sort("roas")}>ROAS{arrow("roas")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <Fragment key={r.id}>
              <tr className={`border-b border-border/40 hover:bg-secondary/20 transition-colors ${openIds.has(r.id) ? "bg-secondary/20" : ""}`}>
                <td className="py-3.5 pl-6 pr-4 min-w-[260px] max-w-[420px] font-medium">{showCreative ? <div className="flex items-start"><CreativeToggle open={openIds.has(r.id)} onClick={() => toggleOpen(r.id)} /><CopyName text={r.name} className="min-w-0 flex-1" /></div> : <CopyName text={r.name} />}</td>
                {showAdset && <td className="py-3.5 px-3 min-w-[180px] max-w-[300px]"><CopyName text={r.adsetName} /></td>}
                <td className="py-3.5 px-3 max-w-[200px] truncate text-muted-foreground" title={r.campaignName}>{r.campaignName}</td>
                <td className="py-3.5 px-3 text-right tabular-nums whitespace-nowrap">{money2(r.spend)}</td>
                <td className="py-3.5 px-3 text-right tabular-nums">{r.leads.toLocaleString()}</td>
                <td className="py-3.5 px-3 text-right tabular-nums">{r.closes.toLocaleString()}</td>
                <td className="py-3.5 px-3 text-right tabular-nums whitespace-nowrap">{money(r.cash)}</td>
                <td className="py-3.5 px-3 pr-6 text-right tabular-nums whitespace-nowrap font-medium text-[var(--brand-primary)]">{roasText(r.cash, r.spend)}</td>
              </tr>
              {showCreative && openIds.has(r.id) && (
                <tr className="border-b border-border/40"><td colSpan={colCount} className="p-0"><AdCreativePanel adId={r.id} />{showAdset && <SalesCallsPanel adId={r.id} />}</td></tr>
              )}
              </Fragment>
            ))}
            <tr className="border-t-2 border-border bg-secondary/10 font-semibold">
              <td className="py-3.5 pl-6 pr-4 text-xs uppercase tracking-wide text-muted-foreground">Totals ({rows.length})</td>
              {showAdset && <td className="py-3.5 px-3 text-xs">—</td>}
              <td className="py-3.5 px-3 text-xs">—</td>
              <td className="py-3.5 px-3 text-right tabular-nums text-xs whitespace-nowrap">{money2(tSpend)}</td>
              <td className="py-3.5 px-3 text-right tabular-nums text-xs">{tLeads.toLocaleString()}</td>
              <td className="py-3.5 px-3 text-right tabular-nums text-xs">{tCloses.toLocaleString()}</td>
              <td className="py-3.5 px-3 text-right tabular-nums text-xs whitespace-nowrap">{money(tCash)}</td>
              <td className="py-3.5 px-3 pr-6 text-right tabular-nums text-xs font-semibold text-[var(--brand-primary)]">{roasText(tCash, tSpend)}</td>
            </tr>
            {footerRows.filter((f) => f.cash > 0).map((f) => (
              <tr key={f.label} className="border-t border-border/40 text-muted-foreground">
                <td className="py-2.5 pl-6 pr-4 text-xs italic" colSpan={showAdset ? 6 : 5}>{f.label}</td>
                <td className="py-2.5 px-3 text-right tabular-nums text-xs whitespace-nowrap">{money(f.cash)}</td>
                <td className="py-2.5 px-3 pr-6 text-right text-xs">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const FUNNEL_OPTIONS: Array<{ value: FunnelFilter; label: string }> = [
  { value: "all", label: "All Funnels" },
  { value: "webinar", label: "Webinar" },
  { value: "bookCall", label: "Book Call" },
];

function FunnelToggle({ value, onChange }: { value: FunnelFilter; onChange: (v: FunnelFilter) => void }) {
  return (
    <div className="inline-flex items-center rounded-xl border border-border bg-card p-1" role="group" aria-label="Funnel Type">
      <span className="px-2 text-[11px] uppercase tracking-wide text-muted-foreground">Funnel</span>
      {FUNNEL_OPTIONS.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${value === o.value ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const FUNNEL_KEY = "uu-ads-roas-funnel";

export function AdsRoasBreakdown({ onData }: { onData?: (d: RoasBreakdownData | null) => void }) {
  const { formatForApi } = useDateRange();
  const [data, setData] = useState<RoasBreakdownData | null>(null);
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnelState] = useState<FunnelFilter>(() => {
    try { const v = localStorage.getItem(FUNNEL_KEY); return v === "webinar" || v === "bookCall" ? v : "all"; } catch { return "all"; }
  });
  const setFunnel = (v: FunnelFilter) => { setFunnelState(v); try { localStorage.setItem(FUNNEL_KEY, v); } catch { /* ignore */ } };
  const { startDate, endDate } = formatForApi();

  // The performance table above always reads the all-funnel numbers (an ad's ROAS doesn't change with the filter)
  useEffect(() => {
    if (funnel === "all") return;
    let cancelled = false;
    fetch(`/api/ads/roas-breakdown?${new URLSearchParams({ startDate, endDate, funnel: "all" })}`)
      .then((r) => r.json() as Promise<{ success: boolean; data?: RoasBreakdownData }>)
      .then((j) => { if (!cancelled) onData?.(j.success && j.data ? j.data : null); })
      .catch(() => { /* table keeps its last value */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, funnel === "all"]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/ads/roas-breakdown?${new URLSearchParams({ startDate, endDate, funnel })}`)
      .then((r) => r.json() as Promise<{ success: boolean; data?: RoasBreakdownData }>)
      .then((j) => { if (!cancelled) { const d = j.success && j.data ? j.data : null; setData(d); if (funnel === "all") onData?.(d); } })
      .catch((err: unknown) => { console.error("Error fetching ROAS breakdown:", err); if (!cancelled) { setData(null); if (funnel === "all") onData?.(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, funnel]);

  if (loading) return <div className="bg-card rounded-2xl border border-border p-6"><div className="h-5 w-48 bg-secondary rounded mb-5 animate-pulse" /><div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-secondary/30 rounded animate-pulse" />)}</div></div>;
  if (!data) return null;
  const t = data.totals;
  const m = data.metrics;
  const stat = (label: string, value: string, accent?: boolean, title?: string, sub?: string) => (
    <div key={label} className="bg-card rounded-xl border border-border px-4 py-3" title={title}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold tabular-nums mt-1 ${accent ? "text-[var(--brand-primary)]" : ""}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">{sub}</p>}
    </div>
  );
  const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v.toFixed(1)}%`);
  const usd = (v: number | null | undefined) => (v === null || v === undefined ? "—" : money(v));
  const usd2 = (v: number | null | undefined) => (v === null || v === undefined ? "—" : money2(v));
  const xTimes = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v.toFixed(2)}x`);
  const webinarNote = data.webinars.length > 0
    ? `Webinar cash from paid opt-ins of the webinar${data.webinars.length > 1 ? "s" : ""} dated in this range (${data.webinars.map((w) => w.name).join(", ")}), same as the ROAS card.`
    : "No webinar is dated in this range, so no webinar cash is attributed.";
  const bookCallNote = "Book Call cash = cash collected (EOC) from leads who applied / self-booked through the ads iClosed funnel in this range.";
  const note = funnel === "bookCall" ? bookCallNote : funnel === "webinar" ? webinarNote : `${webinarNote} ${bookCallNote}`;
  const sf = data.spendByFunnel;
  const leadLabel = funnel === "webinar" ? "Cost per Registrant" : "Cost per Lead";

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Paid ROAS Breakdown</h2>
          <p className="text-xs text-muted-foreground mt-1">{note}</p>
          {sf && <p className="text-xs text-muted-foreground mt-1">Spend in range: Webinar {money2(sf.webinar)} · Book Call {money2(sf.bookCall)} (Meta campaigns with "Call" in the name are Book Call)</p>}
        </div>
        <FunnelToggle value={funnel} onChange={setFunnel} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {stat("Paid ROAS", t.paidRoas !== null && t.paidCash > 0 ? `${t.paidRoas.toFixed(2)}x` : "—", true)}
        {stat("Paid Cash", money(t.paidCash))}
        {stat("Spend", money2(t.spend))}
        {stat("Tied to Exact Ad", money(t.exactAdCash))}
        {stat("Ad Set Only", money(t.adsetOnlyCash))}
        {stat("Paid, No Ad Tag", money(t.untaggedPaidCash), false, "Paid opt-ins with no ad or ad set ID in the link (empty utm_content/utm_term)")}
        <ClosedLeadsCard startDate={startDate} endDate={endDate} funnel={funnel} />
      </div>
      {m && (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold pt-1">Scaling Metrics · Front End</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {stat("CPM", usd2(m.cpm), false, "Spend ÷ impressions × 1,000 (Meta). Jeremy: ~$30–80 is a healthy range.", `${m.impressions.toLocaleString()} impressions`)}
            {stat("Link CTR", pct(m.linkCtr), false, "Link clicks ÷ impressions (Meta). Jeremy: 2% is the ideal floor.", `${m.linkClicks.toLocaleString()} link clicks`)}
            {stat(leadLabel, usd2(m.costPerLead), false, "Spend ÷ paid leads (webinar paid opt-ins + book-call ad applications). Jeremy (webinar registrant): $5–10 elite, $20–30 average, $40+ below average.", `${m.leads.toLocaleString()} paid leads`)}
            {stat("Booked Calls", m.bookedCalls.toLocaleString(), false, "Paid leads with a booked call (meeting booking or a closer EOC after they came in).", `${pct(m.bookingRate)} of leads booked`)}
            {stat("Cost per Booked Call", usd2(m.costPerBookedCall), true, "Spend ÷ booked calls. Jeremy: the big front-end number; compare it to Cash per Booked Call.")}
            {funnel !== "bookCall" && m.webinarRegistrants > 0
              ? stat("Webinar Show Rate", pct(m.webinarShowRate), false, "Zoom attendees ÷ registrants for the webinars in range (all registrants, paid + organic). Jeremy: 20% average, 30% good.", `${m.webinarAttended.toLocaleString()} of ${m.webinarRegistrants.toLocaleString()} attended`)
              : stat("Lead → Call", pct(m.bookingRate), false, "Booked calls ÷ paid leads.")}
          </div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold pt-2">Scaling Metrics · Back End</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {stat("Call Show Rate", pct(m.showRate), false, "Attributed EOC calls taken ÷ attributed EOC calls due in the selected period. MRR and rescheduled EOCs are excluded; no-shows and cancellations count due but not taken.", `${m.showedCalls.toLocaleString()} of ${m.callsDue.toLocaleString()} due calls showed`)}
            {stat("Close Rate", pct(m.closeRate), false, "Closes ÷ showed calls. Jeremy: webinar ~40% avg (50% above avg); book call 30–35% respectable.", `${m.closes.toLocaleString()} closes · CPA ${usd(m.cpa)}`)}
            {stat("AOV", usd(m.aov), false, "Average order value = contract revenue of the paid closes ÷ closes (EOC revenue generated).", `cash per close ${usd(m.cashPerClose)}`)}
            {stat("Cash per Booked Call", usd(m.cashPerBookedCall), true, "Paid cash collected ÷ booked calls. Jeremy's #1 scaling number.", `per showed call ${usd(m.cashPerShow)}`)}
            {stat(funnel === "webinar" ? "Cash per Registrant" : "Cash per Lead", usd2(m.cashPerLead), false, "Paid cash collected ÷ paid leads.")}
            {stat("Cash ÷ Cost per Call", xTimes(m.callRatio), true, "Cash per booked call ÷ cost per booked call. Jeremy: 4–5x means it's safe to scale; compressing toward 2x as spend rises = you've hit the ceiling.", "target 4–5x")}
          </div>
        </div>
      )}
      <RoasTable
        title="ROAS by Ad Set"
        subtitle="Every paid cash dollar tied to an ad set, including cash tied to an exact ad, rolled up to its ad set."
        rows={data.adsets}
        nameLabel="Ad Set"
        showAdset={false}
        footerRows={[{ label: "Paid, but no ad / ad set tag on the opt-in / application", cash: t.untaggedPaidCash }]}
      />
      <RoasTable
        title="ROAS by Specific Ad"
        subtitle="Only cash whose opt-in link carried this exact ad (ad ID, or an ad set that ran one ad). No estimates."
        rows={data.ads}
        nameLabel="Ad"
        showAdset
        showCreative
        footerRows={[
          { label: "Tied to an ad set with several ads, not to one ad (see ROAS by Ad Set)", cash: t.adsetOnlyCash },
          { label: "Paid, but no ad / ad set tag on the opt-in / application", cash: t.untaggedPaidCash },
        ]}
      />
    </div>
  );
}

export default AdsRoasBreakdown;
